import React, { FC } from 'react';
import { Box, Dialog, makeStyles, Theme, useMediaQuery, Typography, Divider, Grid } from '@material-ui/core';
import { CloseIcon } from 'svgs';
import { InfoSlideProps } from './InfoSlider';
import Image from '../../../blog/components/Image';

export interface InfoPopupProps {
  open: boolean;
  item?: InfoSlideProps;
  onClose: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.4)' },
  },
  paper: {
    overflowY: 'unset',
    borderRadius: 0,
    minWidth: 345,
    width: '99%',
    overflowX: 'unset',
    backgroundColor: theme.palette.common.white,
    boxShadow: 'none',
    margin: theme.spacing(4, 0),
    [theme.breakpoints.up('sm')]: {
      maxWidth: 1000,
    },
  },
  title: {
    padding: theme.spacing(2.5, 3),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.common.white,
    maxHeight: 50,
  },
  icon: {
    backgroundColor: theme.palette.primary.main,
    padding: 11,
    right: 0,
    top: 0,
    cursor: 'pointer',
    maxHeight: 50,
  },
  name: {
    fontWeight: 500,
    color: theme.palette.primary.dark,
  },
  nameWithoutDescription: {
    fontSize: 50,
    fontWeight: 500,
    color: theme.palette.primary.dark,
  },
  positionWithoutDescription: {
    fontWeight: 500,
    color: theme.palette.primary.light,
    fontSize: 25,
  },
  position: {
    fontWeight: 500,
    color: theme.palette.primary.light,
  },
  divider: {
    maxWidth: 100,
    height: 1,
    backgroundColor: theme.palette.primary.main,
    marginBottom: theme.spacing(4),
  },
  imageWrapper: {
    width: '100%',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: 'auto',
    display: 'block',
    objectFit: 'cover',
  },
}));

const InfoPopup: FC<InfoPopupProps> = (props) => {
  const { open, onClose, item } = props;
  if (item === null) {
    return null;
  }
  const {
    title,
    image,
    popupContent: { description, name, position },
  } = item;
  const classes = useStyles();
  const isLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  return (
    <Dialog
      onClose={onClose}
      open={open}
      disableScrollLock={isLarge}
      scroll={!isLarge ? 'body' : 'paper'}
      className={classes.root}
      classes={{ paper: classes.paper }}
    >
      <Box position="relative">
        <Box position="absolute" className={classes.icon} onClick={onClose}>
          <CloseIcon />
        </Box>
      </Box>
      <Box mt={8} />
      {description ? (
        <Grid container style={{ padding: '20px 30px 60px' }} spacing={5}>
          <Grid item sm={12} md={4}>
            <Box className={classes.imageWrapper}>
              <Image src={image} alt={title} width={330} height={450} className={classes.image} />
            </Box>
          </Grid>
          <Grid item sm={12} md={8}>
            <Typography variant="h4" className={classes.name}>
              {name}
            </Typography>
            <Box mt={2} mb={3}>
              <Typography className={classes.position}>{position}</Typography>
            </Box>
            <Divider className={classes.divider} />
            <Typography color="textSecondary">{description}</Typography>
          </Grid>
        </Grid>
      ) : (
        <Grid container style={{ padding: '20px 30px 60px' }} spacing={5} alignItems="center">
          <Grid item sm={12} md={4}>
            <Box className={classes.imageWrapper}>
              <Image src={image} alt={title} width={330} height={450} className={classes.image} />
            </Box>
          </Grid>
          <Grid item sm={12} md={8}>
            <Typography variant="h4" className={classes.nameWithoutDescription}>
              {name}
            </Typography>
            <Box mt={2} mb={3}>
              <Typography className={classes.positionWithoutDescription}>{position}</Typography>
            </Box>
            <Divider className={classes.divider} />
          </Grid>
        </Grid>
      )}
    </Dialog>
  );
};

export default InfoPopup;
